// Multiplayer ("Gemeinsam zeichnen") domain — pure types + helpers, no I/O.
//
// This is the wire contract for collaborative drawing. The monolith emits local
// strokes through the bridge; the transport adapter (Supabase Realtime broadcast)
// relays them to the room and surfaces remote strokes back. The event/payload
// shapes here mirror the chatpartygames `canvas:*` socket protocol so the two
// implementations stay semantically identical.
//
// Pure module: no SDK, no globals, fully unit-testable.

/** A single canvas coordinate (canvas-space pixels). */
export interface DrawPoint {
  x: number;
  y: number;
}

/** Horizontal scanline segment used by the flood-fill tool. */
export interface FillSegment {
  y: number;
  x1: number;
  x2: number;
}

/**
 * Local stroke-start payload the monolith hands to the bridge. Mirrors the
 * monolith's drawing state (tool/color/effect/fill) so a remote peer can render
 * the stroke identically. `senderId` is stamped by the transport, not the caller.
 */
export interface StrokeStartInput {
  strokeId: string;
  color: string;
  brushSize: number;
  point: DrawPoint;
  tool?: string;
  shapeType?: string;
  lineStyle?: string;
  effect?: string;
  fillSegments?: FillSegment[];
}

export interface StrokeUpdateInput {
  strokeId: string;
  points: DrawPoint[];
}

export interface StrokeEndInput {
  strokeId: string;
}

// ---- Incoming (remote) events: the local shape + the originating client id ----

export interface RemoteStrokeStart extends StrokeStartInput {
  senderId: string;
}
export interface RemoteStrokeUpdate extends StrokeUpdateInput {
  senderId: string;
}
export interface RemoteStrokeEnd extends StrokeEndInput {
  senderId: string;
}
export interface RemoteClear {
  senderId: string;
}
export interface RemoteUndo {
  senderId: string;
  strokeId: string;
}

/** A participant currently present in a room (presence-tracked). */
export interface Participant {
  id: string;
}

/**
 * Broadcast event names on the realtime channel. Kept short + stable; both the
 * adapter and any future server relay key off these exact strings.
 */
export const MP_EVENTS = {
  strokeStart: 'stroke:start',
  strokeUpdate: 'stroke:update',
  strokeEnd: 'stroke:end',
  clear: 'clear',
  undo: 'undo',
} as const;

export type MpEventName = (typeof MP_EVENTS)[keyof typeof MP_EVENTS];

/** Prefix for all collaborative-drawing realtime channels. */
const ROOM_CHANNEL_PREFIX = 'draw-room';

/**
 * Normalise a user-supplied room id into a safe channel-name component.
 * Keeps `[a-z0-9-]`, lower-cases, collapses runs, trims to 64 chars. Empty or
 * all-invalid input yields '' so callers can reject it.
 */
export function sanitizeRoomId(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 64);
}

/** Build the realtime channel name for a room (after sanitising the id). */
export function roomChannelName(roomId: string): string {
  return `${ROOM_CHANNEL_PREFIX}:${sanitizeRoomId(roomId)}`;
}

/**
 * Generate a short, shareable room id (no crypto needed — collision risk is
 * acceptable for ad-hoc drawing rooms). Accepts a 0..1 random source so it stays
 * pure/testable. 8 chars from a 32-symbol alphabet ≈ 40 bits.
 */
export function makeRoomId(random: () => number): string {
  const alphabet = '23456789abcdefghjkmnpqrstuvwxyz'; // no 0/1/o/l/i ambiguity
  let id = '';
  for (let i = 0; i < 8; i++) {
    const idx = Math.floor(random() * alphabet.length) % alphabet.length;
    id += alphabet.charAt(idx);
  }
  return id;
}
